import {
  bootstrapSession,
  selectHasSession,
  selectIsAuthenticated,
  selectIsSessionUnresolved,
  selectMustChangePin,
  selectPermissions,
  type WithAuthState,
} from '@saniplus/api-client';
import type { Permission } from '@saniplus/contracts';
import { useEffect, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router';

/**
 * The route guards, shared because both apps enforce exactly the same rules
 * and a divergence between them would be an authorization bug, not a styling
 * difference.
 *
 * None of these is a security control: the API answers 401/403/404 on its own
 * and out-of-scope reads answer 404 by design. They exist so the UI never
 * offers a door that is already locked.
 */

export const LOGIN_PATH = '/login';
export const PIN_CHANGE_PATH = '/pin';

/** Where an unauthenticated visitor was trying to go, for the post-login bounce. */
export interface LocationState {
  readonly from?: string;
}

interface DispatchLike {
  (action: unknown): unknown;
}

/**
 * Runs `bootstrapSession` once on mount and holds the tree until the answer
 * is in. Without it the router would decide "anonymous" during the refresh
 * round trip and bounce a signed-in user to the login screen on every reload.
 */
export function SessionGate({ fallback, children }: { fallback: ReactNode; children: ReactNode }) {
  const dispatch = useDispatch() as DispatchLike;
  const unresolved = useSelector(selectIsSessionUnresolved);

  useEffect(() => {
    dispatch(bootstrapSession());
  }, [dispatch]);

  return <>{unresolved ? fallback : children}</>;
}

/**
 * Everything behind it requires a session.
 *
 * `selectHasSession`, not `selectIsAuthenticated`: an installed PWA reopened
 * with no signal rehydrates its identity from IndexedDB (`status: 'offline'`,
 * no token) and must stay in the shell. Bouncing it to a login screen that
 * needs the very network it lacks strands the punch already queued under that
 * worker's user id. Nothing is granted by passing here — the API still answers
 * every request on its own, and there is no token to send it. Only the worker
 * PWA can ever be in that state: rehydration is opted into per app, and
 * `apps/admin-web` does not opt in.
 */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const location = useLocation();
  const hasSession = useSelector(selectHasSession);

  if (!hasSession) {
    // `from` is a **pathname only**, never a full URL. Nothing reads it today;
    // a consumer that starts to must accept only a value beginning with a
    // single `/` (not `//`, which a browser resolves as a host) — otherwise
    // this becomes an open redirect on the login screen.
    return (
      <Navigate to={LOGIN_PATH} replace state={{ from: location.pathname } satisfies LocationState} />
    );
  }
  return <>{children ?? <Outlet />}</>;
}

/**
 * D-05 / §6: while `mustChangePin` is set the API answers 403
 * `PIN_CHANGE_REQUIRED` for every route but refresh, logout, pin/change and
 * me — so the router hard-redirects rather than letting screens fail one by
 * one.
 */
export function RequirePinChanged({ children }: { children?: ReactNode }) {
  const mustChangePin = useSelector(selectMustChangePin);

  if (mustChangePin) return <Navigate to={PIN_CHANGE_PATH} replace />;
  return <>{children ?? <Outlet />}</>;
}

export interface RequirePermissionProps {
  /** The caller must hold every one of these. */
  readonly permission: Permission | Permission[];
  /** Shown instead of the route when the permission is missing. */
  readonly fallback: ReactNode;
  readonly children?: ReactNode;
}

/**
 * D-02: "Nav gates on `permissions[]` from login/`/auth/me`."
 *
 * The fallback is a rendered explanation, never a redirect to the dashboard:
 * silently sending someone somewhere else reads as a broken link.
 */
export function RequirePermission({ permission, fallback, children }: RequirePermissionProps) {
  const held = useSelector(selectPermissions);
  const required = Array.isArray(permission) ? permission : [permission];
  const allowed = required.every((one) => held.includes(one));

  if (!allowed) return <>{fallback}</>;
  return <>{children ?? <Outlet />}</>;
}

/**
 * Redirects an already-signed-in user away from `/login`.
 *
 * `selectIsAuthenticated` on purpose, where `RequireAuth` uses
 * `selectHasSession`: an offline rehydration is not a reason to hold someone
 * away from the login screen. They cannot sign in from there without a network
 * either way, and a worker handing the phone to the next shift must not be
 * locked out of the one screen that changes who is using it.
 */
export function RedirectIfAuthenticated({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  const authenticated = useSelector(selectIsAuthenticated);
  const mustChangePin = useSelector(selectMustChangePin);

  if (authenticated) return <Navigate to={mustChangePin ? PIN_CHANGE_PATH : to} replace />;
  return <>{children}</>;
}

export type { WithAuthState };
